<template>
  <div class="w-full pt-5 pb-5">
    <div style="height: 100%" v-if="loading">
      <loader size="xxs" :loader-image="false" />
    </div>
    <div v-else>
      <div class="flex">
        <back-button
          label="Back"
          @onClick="$router.back()"
          variant="secondary"
          class="mr-4"
        />
        <div
          class="mt-4"
          style="border-left: 1px solid #878e99; height: 1.5rem"
        />

        <div style="width: 280px">
          <h1 class="text-xl text-left font-extrabold ml-4 mt-3">
            Contract Renewal
          </h1>
        </div>

        <div class="w-7/12 mt-4">
          <Breadcrumb :items="breadcrumbs" />
        </div>
      </div>

      <div class="px-3">
        <card class="p-5 mt-6">
          <div class="border border-dashed p-3">
            <div class="flex w-full mb-5">
              <card class="p-3 mr-4 w-1/3">
                <p class="text-xs font-black uppercase text-romanSilver">
                  Engagement Date
                </p>
                <p class="font-semibold text-lg text-jet">
                  {{ formatDate(engagementDate) }}
                </p>
              </card>
              <card class="p-3 mr-4 w-1/3">
                <p class="text-xs font-black uppercase text-romanSilver">
                  Employment Type
                </p>
                <p class="font-semibold text-lg text-darkPurple">
                  {{ displayValue(employmentType) }}
                </p>
              </card>
              <card class="p-3 w-1/3">
                <p class="text-xs font-black uppercase text-romanSilver">
                  Contract End Date
                </p>
                <p class="font-semibold text-lg text-darkPurple">
                  {{ formatDate(contractEndDate) }}
                </p>
              </card>
            </div>

            <div class="flex w-full mb-3">
              <card class="p-3 w-1/3">
                <p class="text-xs font-black uppercase text-romanSilver">
                  New Contract End Date
                </p>
                <p class="font-semibold text-lg text-darkPurple">
                  {{ formatDate(newContractEndDate) }}
                </p>
              </card>
            </div>

            <div v-if="fileUrl" class="mt-2">
              <span
                class="text-flame text-sm font-semibold cursor-pointer"
                @click="openSupportingDocument"
              >
                View supporting document(s)
              </span>
            </div>
          </div>
        </card>

        <card class="p-5 mt-4">
          <div class="my-4" style="border: 0.2px solid #878e99" />
          <div class="w-full flex">
            <div class="flex flex-col flex-grow">
              <p class="text-base font-semibold">Submitted By:</p>
              <div class="flex flex-grow py-2 items-start">
                <img
                  v-if="submittedBy.photo != null && submittedBy.photo != ''"
                  class="mr-2 object-cover"
                  alt="photo"
                  style="width: 82px; height: 82px; border-radius: 5px"
                  :src="submittedBy.photo"
                />
                <div
                  style="height: 82px; width: 82px; border-radius: 5px"
                  class="text-blueCrayola border flex items-center justify-center font-semibold text-3xl mr-2"
                  v-else
                >
                  {{
                    $getInitials(
                      `${submittedBy.fname || ""} ${submittedBy.lname || ""}`
                    )
                  }}
                </div>
                <div class="flex flex-col">
                  <div class="flex flex-col">
                    <span class="text-lg text-optimaBlack font-bold mr-1">
                      {{ `${submittedBy.fname || ""} ${submittedBy.lname || ""}` }}
                    </span>
                    <span class="text-lg text-blueCrayola font-bold">
                      {{
                        submittedBy.userDesignation
                          ? submittedBy.userDesignation.name
                          : ""
                      }}
                    </span>
                    <span
                      v-if="submittedAt"
                      class="text-sm text-romanSilver mt-1"
                    >
                      {{ formatDate(submittedAt) }}
                    </span>
                  </div>
                </div>
                <div class="border h-20 mx-5" style="width: 1px" />
                <div class="flex items-center h-[82px] gap-2" style="height: 62px;">
                  <icon
                    icon-name="icon-message-outline"
                    size="xs"
                    class-name="text-flame"
                  />
                  <span class="text-flame text-base font-semibold">
                    Send Message
                  </span>
                </div>
              </div>
            </div>

            <div class="flex flex-col flex-grow">
              <p class="text-base font-semibold ">Renewal For:</p>
              <div class="flex">
                <div class="flex flex-grow py-3 items-start">
                  <img
                    v-if="renewalFor.photo != null && renewalFor.photo != ''"
                    class="mr-2 object-cover"
                    alt="photo"
                    style="width: 82px; height: 82px; border-radius: 5px"
                    :src="renewalFor.photo"
                  />
                  <div
                    style="height: 82px; width: 82px; border-radius: 5px"
                    class="text-blueCrayola border flex items-center justify-center font-semibold text-3xl mr-2"
                    v-else
                  >
                    {{
                      $getInitials(
                        `${renewalFor.fname || ""} ${renewalFor.lname || ""}`
                      )
                    }}
                  </div>
                  <div class="flex flex-col">
                    <p class="text-lg font-bold text-darkPurple">
                      {{ `${renewalFor.fname || ""} ${renewalFor.lname || ""}` }}
                    </p>
                    <span class="flex my-1">
                      <p
                        class="font-[900] uppercase text-romanSilver mr-3 mt-1 text-xs font-black uppercase text-romanSilver"
                        style="font-size: 10px"
                      >
                        Position:
                      </p>
                      <p class="font-semibold uppercase text-sm text-jet">
                        {{ positionLabel }}
                      </p>
                    </span>
                    <span class="flex my-1">
                      <p
                        class="font-[900] uppercase text-romanSilver mr-3 mt-1 text-xs font-black uppercase text-romanSilver"
                        style="font-size: 10px"
                      >
                        Organizational Unit:
                      </p>
                      <p class="font-semibold uppercase text-sm text-jet">
                        {{ organisationalUnit }}
                      </p>
                    </span>
                    <span class="flex my-1">
                      <p
                        class="font-[900] uppercase text-romanSilver mr-3 mt-1 text-xs font-black uppercase text-romanSilver"
                        style="font-size: 10px"
                      >
                        Office Location:
                      </p>
                      <p class="font-semibold uppercase text-sm text-jet">
                        {{ officeLocation }}
                      </p>
                    </span>
                    <span class="flex my-1">
                      <p
                        class="font-[900] uppercase text-romanSilver mr-3 mt-1 text-xs font-black uppercase text-romanSilver"
                        style="font-size: 10px"
                      >
                        Length of Service:
                      </p>
                      <p class="font-semibold uppercase text-sm text-jet">
                        {{ lengthOfService }}
                      </p>
                    </span>
                    <span class="flex my-1">
                      <p
                        class="font-[900] uppercase text-romanSilver mr-3 mt-1 text-xs font-black uppercase text-romanSilver"
                        style="font-size: 10px"
                      >
                        Manager:
                      </p>
                      <p class="font-semibold uppercase text-sm text-jet">
                        {{ managerName }}
                      </p>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </card>

        <card class="p-5 mt-4">
          <div class="w-full flex">
            <div class="flex flex-col flex-grow py-2">
              <p class="text-lg font-bold">Approvers ({{ approversNumber }})</p>
              <p class="text-sm">
                You can quickly approve or disapprove from here.
              </p>
              <p class="text-sm">
                You also see other approvers’ responses and comments within the
                approval trail.
              </p>
            </div>
            <div class="flex flex-grow flex-col">
              <span class="flex">
                <p
                  class="font-bold text-xl"
                  :class="
                    approvalPercentage >= 100
                      ? 'text-mediumSeaGreen'
                      : 'text-darkPurple'
                  "
                >
                  {{ Math.ceil(approvalPercentage) }}%
                </p>
                <p class="text-romanSilver ml-1 mt-1 text-sm">Completed</p>
              </span>
              <ProgressBar
                :value="approvalPercentage"
                class="mt-2"
              />
              <p class="font-semibold text-xs mt-2 text-romanSilver uppercase">
                {{ approvalsData.requests?.approversLeft || 0 }} approvers left
              </p>
            </div>
            <div class="flex flex-grow justify-end items-center">
              <Button
                class="text-flame border border-solid border-flame font-semibold text-center"
                width="8rem"
                style="height: 32px;"
                @click="displayProgress()"
              >
                See Comments
              </Button>
            </div>
          </div>
        </card>

        <div
          class="w-full mt-5 flex"
          v-if="
            approverDetails.userId === $AuthUser.id &&
            !$store.getters.hasSubscriptionExpired()
          "
        >
          <CTextarea
            placeholder="Insert Comment Here"
            variant="primary"
            class="mt-2"
            value=""
            :row="8"
            :height="90"
            :col="130"
            v-model="comment"
          />
        </div>
      </div>

      <div
        class="pl-3 pr-3 mt-6 flex flex-row justify-between items-center button-layout"
        v-if="
          approverDetails.userId === $AuthUser.id &&
          !$store.getters.hasSubscriptionExpired()
        "
      >
        <div class="flex flex-row items-center">
          <Button
            class="font-semibold border text-center mr-6 text-white"
            :background="appearance.buttonColor || defaultBTNColor"
            width="7rem"
            style="height: 44px"
            @click="approveRequest"
            :disabled="disableBtn"
          >
            {{ approverDetails.actionType === "approver" ? "Approve" : "Submit" }}
          </Button>
          <Button
            v-if="approverDetails.actionType === 'approver'"
            class="text-dynamicBackBtn border border-solid border-dynamicBackBtn font-semibold text-center"
            width="7rem"
            style="height: 44px;"
            @click="disapproveRequest"
            :disabled="disableBtn"
          >
            Disapprove
          </Button>
        </div>
        <span
          class="text-flame text-base font-semibold cursor-pointer"
          @click="putOnHold"
        >
          Put On Hold
        </span>
      </div>
    </div>

    <RightSideBar v-if="seeComments" @close="seeComments = false" close-button>
      <template v-slot:title>
        <p class="text-darkPurple">Approvers ({{ approversNumber }})</p>
      </template>
      <template v-slot:subtitle>
        <p class="font-semibold text-darkPurple pb-4">
          See other approvers comments to this request.
        </p>
      </template>
      <div>
        <div v-for="(item, index) in requestProgress" :key="index">
          <ApproversCard
            class="flex bg-white pt-2 px-3 pb-4 mb-2 even:bg-ghostWhite border-l-4"
            :style="approveDetails(item.currentApprover)"
          >
            <div class="flex w-full">
              <div class="flex w-auto pr-1 py-2 inline-block">
                <img
                  v-if="item.photo != null && item.photo != ''"
                  class="round-md"
                  style="width: 40px; height: 40px"
                  :src="item.photo"
                />
                <div
                  style="height: 40px; width: 40px; border-radius: 5px"
                  class="text-blueCrayola border text-center font-semibold pt-2 mr-2"
                  v-else
                >
                  {{ $getInitials(`${item.fname} ${item.lname}`) }}
                </div>
              </div>
              <div class="flex flex-col w-3/4 mt-2">
                <div class="leading-tight">
                  <span class="block text-lg leading-tight">
                    <span class="block text-base"
                      >{{ item.fname }} {{ item.lname }}</span
                    >
                    <span class="text-romanSilver uppercase text-xs block">
                      {{ item.userDesignation?.name }}
                    </span>
                    <span class="block text-xs" v-if="item.actionTakenAt">
                      {{
                        $DATEFORMAT(new Date(item.actionTakenAt), "MMMM dd, yyyy")
                      }}
                    </span>
                  </span>
                </div>
                <div class="flex w-full mt-1 leading-tight">
                  <div class="w-full">
                    <span
                      class="pr-2 text-sm break-words leading-tight"
                      v-if="item.comment != ''"
                    >
                      <span class="font-normal text-romanSilver leading-tight">
                        Comment:
                      </span>
                      <span class="leading-tight">
                        {{ item.comment }}
                      </span>
                    </span>
                    <span class="leading-tight" v-else> --No Comment-- </span>
                  </div>
                </div>
              </div>
              <div class="flex-none w-4/12 text-base">
                <Tag
                  class="py-2 px-2 mx-2 float-right mt-8 text-sm"
                  :class="approveBgColor(item.currentApprover)"
                >
                  {{ approveText(item.currentApprover) }}
                </Tag>
              </div>
            </div>
          </ApproversCard>
        </div>
      </div>
    </RightSideBar>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import Card from "@/components/Card";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import ProgressBar from "@/components/ProgressBar";
import ApproversCard from "@/components/ApproversCard";
import RightSideBar from "@/components/RightSideBar";
import Tag from "@/components/Tag";

export default {
  name: "ContractRenewalApproval",
  components: {
    BackButton,
    Breadcrumb,
    Card,
    Button,
    Icon,
    ProgressBar,
    CTextarea,
    Tag,
    ApproversCard,
    RightSideBar,
  },
  data() {
    return {
      disableBtn: false,
      comment: "",
      loading: true,
      seeComments: false,
      breadcrumbs: [
        {
          disabled: false,
          text: "Contract Renewal",
          href: "contract renewal",
          id: "Contract Renewal",
        },
        {
          disabled: false,
          text: "Approval",
          href: "approval",
          id: "Approval",
        },
        {
          disabled: false,
          text: "Contract Renewal",
          id: "Contract Renewal Details",
        },
      ],
      contractDetails: {},
      renewalEmployee: {},
      approverDetails: {},
      approversNumber: "",
      approvalsData: {},
      requestProgress: [],
    };
  },
  computed: {
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
    approvalPercentage() {
      return Number(this.approvalsData?.requests?.approvalPercentage || 0);
    },
    latestRequest() {
      return (
        this.contractDetails.latestRequest ||
        this.contractDetails.request ||
        this.contractDetails ||
        {}
      );
    },
    submittedBy() {
      const initiator = this.latestRequest.initiator;
      if (initiator?.fname || initiator?.lname) {
        return {
          ...this.approvalsData,
          fname: initiator.fname,
          lname: initiator.lname,
          photo: this.approvalsData.photo || initiator.photo || null,
          userDesignation:
            this.approvalsData.userDesignation || initiator.userDesignation,
        };
      }
      return this.approvalsData || {};
    },
    submittedAt() {
      return (
        this.latestRequest.createdAt ||
        this.approvalsData.createdAt ||
        this.approvalsData.requests?.createdAt ||
        this.approvalsData.dateSubmitted ||
        null
      );
    },
    renewalFor() {
      return this.renewalEmployee || {};
    },
    engagementDate() {
      return (
        this.renewalFor.engagementDate ||
        this.renewalFor.employmentDate ||
        this.latestRequest.engagementDate ||
        null
      );
    },
    employmentType() {
      return (
        this.renewalFor.employmentType ||
        this.renewalFor.employmentTypeName ||
        this.latestRequest.employmentType ||
        null
      );
    },
    contractEndDate() {
      return this.latestRequest.contractEndDate || null;
    },
    newContractEndDate() {
      return this.latestRequest.newContractEndDate || null;
    },
    fileUrl() {
      return this.latestRequest.fileUrl || null;
    },
    notifyUserId() {
      return (
        this.latestRequest.userId ||
        this.renewalFor.userId ||
        this.renewalFor.id ||
        null
      );
    },
    positionLabel() {
      const designation =
        this.renewalFor.userDesignation?.name ||
        this.renewalFor.designation?.name ||
        this.renewalFor.designation ||
        "";
      const func =
        this.renewalFor.orgFunction?.name ||
        this.renewalFor.function?.name ||
        this.renewalFor.function ||
        "";
      if (designation && func) return `${designation}, ${func}`;
      return designation || func || "---";
    },
    organisationalUnit() {
      return (
        this.renewalFor.orgFunction?.name ||
        this.renewalFor.organisationalUnit?.name ||
        this.renewalFor.department?.name ||
        this.renewalFor.function?.name ||
        this.renewalFor.function ||
        "---"
      );
    },
    officeLocation() {
      const office =
        this.renewalFor.employeeOffice ||
        this.renewalFor.office ||
        this.renewalFor.officeLocation;
      if (!office) return "---";
      if (typeof office === "string") return office;
      const parts = [office.name, office.city, office.state].filter(Boolean);
      return parts.length ? parts.join(", ") : "---";
    },
    lengthOfService() {
      return (
        this.renewalFor.lengthOfService ||
        this.latestRequest.lengthOfService ||
        "---"
      );
    },
    managerName() {
      const manager =
        this.renewalFor.userReportingTo ||
        this.renewalFor.manager ||
        this.renewalFor.reportingTo;
      if (!manager) return "---";
      if (typeof manager === "string") return manager;
      const name = `${manager.fname || ""} ${manager.lname || ""}`.trim();
      return name || manager.name || "---";
    },
  },
  methods: {
    displayValue(value) {
      return value || "-";
    },
    formatDate(value) {
      if (!value) return "-";
      try {
        return this.$DATEFORMAT(new Date(value), "dd MMM yyyy");
      } catch (e) {
        return "-";
      }
    },
    openSupportingDocument() {
      if (this.fileUrl) {
        window.open(this.fileUrl, "_blank");
      }
    },
    putOnHold() {
      this.$toasted.info("Put on hold is not available for this request yet.", {
        duration: 3000,
      });
    },
    async loadRenewalEmployee(userId) {
      if (!userId) {
        this.renewalEmployee = {};
        return;
      }
      try {
        const { data } = await this.$_getOneEmployee(userId, "type=all");
        this.renewalEmployee = data || {};
      } catch (e) {
        try {
          const { data } = await this.$_getOneEmployeeEmploymentInfo({
            id: userId,
          });
          this.renewalEmployee = data || {};
        } catch (err) {
          this.renewalEmployee = {};
        }
      }
    },
    approveRequest() {
      this.disableBtn = true;
      const payload = {
        userId: this.$AuthUser.id,
        requestId: this.$route.params.id,
        comment: this.comment,
        requestSlug: this.approverDetails.requestSlug || "contractExtension",
        hasApproved: true,
        whoToNotify: this.notifyUserId,
        orgId: this.$orgId,
      };
      this.$_respondApprovalRequest(payload)
        .then((result) => {
          this.$toasted.success(result.data.message, { duration: 5000 });
          this.$router.back({ name: "MyApprovals" });
          this.disableBtn = false;
        })
        .catch((err) => {
          this.$toasted.error("Request cannot be processed", {
            duration: 5000,
          });
          this.disableBtn = false;
          throw new Error(err);
        });
    },
    disapproveRequest() {
      this.disableBtn = true;
      const payload = {
        userId: this.$AuthUser.id,
        requestId: this.$route.params.id,
        comment: this.comment,
        requestSlug: this.approverDetails.requestSlug || "contractExtension",
        hasApproved: false,
        whoToNotify: this.notifyUserId,
        orgId: this.$orgId,
      };
      this.$_respondApprovalRequest(payload)
        .then((result) => {
          this.$toasted.success("Request disapproved Successfully", {
            duration: 5000,
          });
          this.$router.back({ name: "MyApprovals" });
          this.disableBtn = false;
          return result;
        })
        .catch((err) => {
          this.$toasted.error("Request cannot be processed", {
            duration: 5000,
          });
          this.disableBtn = false;
          throw new Error(err);
        });
    },
    displayProgress() {
      this.seeComments = true;
    },
    getAllRequests() {
      const userId = this.$AuthUser.id;
      const params = "";
      return this.$_getMyApprovalsfilter(
        params,
        userId,
        "?page=1&perPage=1000",
        "&platform=admin"
      )
        .then((result) => {
          const inProgressData = result.data.approvals;
          this.approvalsData =
            inProgressData.find((a) => a.requestId === this.$route.params.id) ||
            {};
        })
        .catch(() => {
          this.error = true;
        });
    },
    getApprovalTurn() {
      this.$_getApprovalProgress(this.$route.params.id)
        .then((result) => {
          const approverDetails = result.data.data;
          this.requestProgress = result.data.data;
          approverDetails.filter((a) => {
            if (
              a.requestId === this.$route.params.id &&
              a.isApprovalForAll === "false"
            ) {
              if (a.userId === this.$AuthUser.id) {
                this.approverDetails = a;
              }
              this.approversNumber = approverDetails.length;
            } else if (
              a.requestId === this.$route.params.id &&
              a.isApprovalForAll === "true"
            ) {
              if (a.currentApprover === "active") {
                this.approverDetails = a;
                this.approversNumber = approverDetails.length;
              }
            }
            return {};
          });
        })
        .catch((err) => {
          throw new Error(err);
        });
    },
    resolveContractUserId() {
      return (
        this.$route.query.userId ||
        this.approvalsData?.requests?.recipientId ||
        this.approvalsData?.recipientId ||
        null
      );
    },
    getDetails() {
      const requestId = this.$route.params.id;
      const userId = this.resolveContractUserId();
      if (!userId || !requestId) {
        this.loading = false;
        return Promise.resolve();
      }

      return this.$_getEmployeeContractExtensionDetails(userId, requestId)
        .then(async (result) => {
          this.contractDetails = result.data?.data || result.data || {};
          const employeeUserId =
            this.contractDetails.latestRequest?.userId || userId;
          await this.loadRenewalEmployee(employeeUserId);
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    approveDetails(value) {
      if (value === "hasApproved") {
        return "border-color: #13B56A;";
      }
      if (value === "hasReviewed" || value === "reviewed") {
        return "border-color: #3B82F6;";
      }
      if (value === "active") {
        return "border-color: #E99323;";
      }
      if (value === "inactive") {
        return "border-color: #333333;";
      }
      return "";
    },
    approveText(value) {
      if (value === "hasApproved") {
        return "Approved";
      }
      if (value === "hasReviewed" || value === "reviewed") {
        return "Reviewed";
      }
      if (value === "active") {
        return "Pending";
      }
      if (value === "inactive") {
        return "Not Started";
      }
      return "";
    },
    approveBgColor(value) {
      if (value === "hasApproved") {
        return "text-mediumSeaGreen bg-light-sea-green";
      }
      if (value === "hasReviewed" || value === "reviewed") {
        return "text-blueCrayola bg-blue-50";
      }
      if (value === "active") {
        return "text-carrotOrange bgCarrot";
      }
      if (value === "inactive") {
        return "text-black bg-gray-200";
      }
      return "";
    },
    getHistory(query) {
      const userId = this.$AuthUser.id;
      return this.$_getApprovalHistory(userId, query, "&platform=admin")
        .then((result) => {
          const inProgressData = result.data.approvals;
          this.approvalsData =
            inProgressData.find((v) => v.requestId === this.$route.params.id) ||
            {};
        })
        .catch(() => {
          this.error = true;
        });
    },
  },
  async mounted() {
    this.loading = true;
    this.getApprovalTurn();
    try {
      if (this.$route.query.slug === "history") {
        await this.getHistory("?page=1&perPage=50");
      } else {
        await this.getAllRequests();
      }
      await this.getDetails();
    } catch (e) {
      this.loading = false;
    }
  },
};
</script>

<style>
.bgCarrot {
  background-color: rgba(233, 147, 35, 0.08);
}
</style>
